/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Tue May 20 2025
 **/

import { useEffect, useRef, useState, useLayoutEffect } from "react";
import styled from "@emotion/styled";
import { isEmpty } from "lodash-es";

import { UnDraggable } from "@/frameworks/libraries/components/Dragger";
import { Column, Icon, Row, Typography } from "@/frameworks/designStuff/components";
import { MessageList, useChatRoomActions, useMessageList, useSpaceChannelState } from "@/frameworks/core/chat";
import { usePersistUser } from "@/frameworks/core/persistence";
import { _T } from "@rfice/language";
import { Each, Maybe } from "@rsupport/libraries";
import { hoverScrollCss } from "@/frameworks/designStuff";
import { useIntersectionObserver } from "@/frameworks/libraries";
import { useChatDataStore } from "@/modules/chat/store";
import { SidebarID } from "@/frameworks/core/commons";
import { useSidebarActions } from "@/frameworks/core/hooks";

import * as S from "../emotion";
import { ChatItem } from "../../SidebarContents";
import { Message, ScrollButton, useHasScroll, useScrollButtonState } from "../MessageList/private";
import { useMiniChatPopup } from "../../SidebarContents/ChatView/MiniChatPopup/private";

type ThreadMessageListProps = {
	roomID: string;
	variant: ChatUI.Size;
	isMyRoom: boolean;
	myRoomID?: string;
	spaceID?: string;
	threadID: string;
	notice?: WebServices.Chat.GetRoom.Data["notice"];
	voteNotice?: WebServices.Chat.GetRoom.Data["voteNotice"];
	blockedUsers?: string[];
	messageID?: string;
	onShowRecentMessage?: (message: MessageList[string]) => void;
	onShowMarkImage?: (isShow: boolean) => void;
	activityMessageID?: string;
};

export default function ThreadMessageList({
	roomID,
	threadID,
	variant,
	onShowRecentMessage,
	activityMessageID,
	...rest
}: ThreadMessageListProps) {
	const me = usePersistUser("me");
	const bodyRef = useRef<HTMLElement>(null);
	const firstInListNewMessage = useRef<HTMLLIElement | null>(null);
	const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const [isFirstRender, setIsFirstRender] = useState(true);
	const { threadMessageRoot, threadMessageList, threadHasNextPage, threadRefetch } = useMessageList(
		true,
		roomID,
		threadID
	);
	const chatRoomActions = useChatRoomActions();
	const messageListSortedByCreatedDate = Object.values(threadMessageList).sort(
		(messageA, messageB) => messageA.createdAt - messageB.createdAt
	);
	const sidebarActions = useSidebarActions();

	const { scrollButtonState, changeScrollButtonState } = useScrollButtonState();
	const { ref: containerRef, isShowScrollDown } = useHasScroll([messageListSortedByCreatedDate.length]);
	const space = useSpaceChannelState(rest.spaceID ?? "");
	const { handleClose } = useMiniChatPopup({
		id: roomID,
		spaceID: rest.spaceID,
		groupID: space?.groupId || "",
		threadID: threadID
	});

	const targetActivityMessageID = activityMessageID === "auto" ? undefined : activityMessageID;

	const { observerRef: lastChatRef, isShow: isShowLastChat } = useIntersectionObserver<HTMLLIElement>(
		(node) => {
			if (node.isIntersecting) {
				const lastMessage = Object.values(messageListSortedByCreatedDate).slice(-1)[0];
				onShowRecentMessage?.(lastMessage);
			}
		},
		[scrollButtonState.newMessage, messageListSortedByCreatedDate.length],
		{
			threshold: [0, 0.25, 0.5, 0.75, 1],
			root: containerRef.current
		}
	);

	const scrollToBottom = () => {
		if (!containerRef.current) return;
		lastChatRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });

		firstInListNewMessage.current = null;
	};

	const observer = new ResizeObserver(() => {
		if (isShowLastChat) {
			changeScrollButtonState("clear");
		} else {
			if (!scrollButtonState.newMessage) {
				if (isShowScrollDown) changeScrollButtonState("arrow");
				else changeScrollButtonState("clear");
			}
		}
	});

	const goBackMessage = () => {
		// TODO 코드 리팩토링 필요.
		switch (variant) {
			case "fullSize": {
				useChatDataStore.getState().clearThreadID();
				setTimeout(() => {
					const el = document.getElementById(`${threadID}`);
					el?.scrollIntoView({ behavior: "smooth", block: "start" });
				}, 500);

				if (activityMessageID) sidebarActions.hide(SidebarID.ActivityFullSizeThread);
				break;
			}
			case "miniPopup": {
				handleClose();

				const openParams = rest.spaceID
					? {
							channelID: roomID,
							spaceID: rest.spaceID,
							type: "Space" as const
						}
					: {
							channelID: roomID,
							type: "DM" as const
						};

				chatRoomActions.common.open(openParams).then(() => {
					setTimeout(() => {
						const el = document.getElementById(`${threadID}`);
						el?.scrollIntoView({ behavior: "smooth", block: "start" });
					}, 500);
				});

				break;
			}
		}
	};

	useEffect(() => {
		bodyRef.current && observer.observe(bodyRef.current);
		return () => {
			bodyRef.current && observer.unobserve(bodyRef.current);
		};
	}, [isShowLastChat, scrollButtonState.newMessage, isShowScrollDown]);

	const checkScroll = () => {
		if (!containerRef.current) return;
		const el = containerRef.current;
		const hasVerticalScroll = el.scrollHeight > el.clientHeight;
		const isAtTop = el.scrollTop === 0;
		if (rest?.onShowMarkImage) {
			if (!hasVerticalScroll || isAtTop) {
				rest?.onShowMarkImage(false);
			} else {
				rest?.onShowMarkImage(true);
			}
		}
	};

	useEffect(() => {
		checkScroll();
	}, []);

	// threadID가 같은 경우 thread data를 fetch하지 않기 때문에 수동으로 refetch 한다
	useEffect(() => {
		if (threadID) threadRefetch();
	}, [threadID]);

	/* scoll to target thread */
	useLayoutEffect(() => {
		if (targetActivityMessageID) scrollToView(targetActivityMessageID);
	}, [activityMessageID]);

	const scrollToView = (messageId: string) => {
		// clear timeout
		if (scrollTimeoutRef.current) {
			clearTimeout(scrollTimeoutRef.current);
			scrollTimeoutRef.current = null;
		}

		scrollTimeoutRef.current = setTimeout(() => {
			const messageElement = document.getElementById(messageId);
			if (!messageElement) return;

			// scroll to target el
			messageElement.scrollIntoView({ behavior: "smooth", block: "center" });

			// add flash anim class
			messageElement.classList.add("flash-animation");

			// remove class on end
			const handleAnimationEnd = () => {
				messageElement.classList.remove("flash-animation");
				messageElement.removeEventListener("animationend", handleAnimationEnd);
			};
			messageElement.removeEventListener("animationend", handleAnimationEnd);
			messageElement.addEventListener("animationend", handleAnimationEnd);
		}, 500);
	};

	if (!threadMessageRoot && !messageListSortedByCreatedDate) return;
	return (
		<UnDraggable>
			<S.ThreadTotal variant={variant}>
				<Row verticalAlign="center" gap={variant === "fullSize" ? "8px" : "4px"}>
					<Icon name="Left24" style={{ cursor: "pointer" }} onClick={goBackMessage} />
					<Typography typoColor="neutral_90_100" typoSize={variant === "fullSize" ? "Head6" : "Title3"}>
						{_T("스레드")}
					</Typography>
				</Row>
			</S.ThreadTotal>
			<S.Body variant={variant} verticalAlign="bottom" reverse={variant === "transparent"} ref={bodyRef}>
				<StyledList ref={containerRef} variant={variant} gap={variant === "transparent" ? 4 : 0}>
					{threadMessageRoot && (
						<ChatItem
							{...threadMessageRoot}
							{...rest}
							isMessageGroup={false}
							isMe={threadMessageRoot.user?.id === me.id}
							variant={variant}
							threadID={threadID}
							isRootThread={true}
							content={ContentParser(threadMessageRoot.content) as ChatServices.Format2.Message}
						/>
					)}
					<S.ThreadDivider gap={12} verticalAlign="center">
						<S.Line />
						<Typography typoColor="neutral_60_100" typoSize="Body5">
							{_T("스레드")}
						</Typography>
						<S.Line />
					</S.ThreadDivider>
					<Each
						useArray
						of={messageListSortedByCreatedDate}
						render={(message, index) => {
							const prevMessage = index > 0 ? messageListSortedByCreatedDate?.[index - 1] : null;
							const isLastChat = index === messageListSortedByCreatedDate?.length - 1;
							return (
								<Message
									ref={lastChatRef}
									key={message.roomMessageId.length > 0 ? message.roomMessageId : `msg-${index}`}
									index={index + 1}
									message={message}
									previousMessage={prevMessage}
									isMyRoom={rest.isMyRoom}
									isLastChat={isLastChat}
									isShowLastChat={true}
									isFirstRender={isFirstRender}
									myRoomID={rest.myRoomID}
									spaceID={rest.spaceID}
									variant={variant}
									threadID={threadID}
									hasNextPage={threadHasNextPage}
									onMount={() => {
										setIsFirstRender(false);
									}}
									onUnMount={() => {
										setIsFirstRender(true);
									}}
									highlightOnLoad={activityMessageID === "auto"}
								/>
							);
						}}
					/>
				</StyledList>
				<Maybe test={!isEmpty(messageListSortedByCreatedDate)}>
					<ScrollButton {...scrollButtonState} onClick={() => scrollToBottom()} />
				</Maybe>
			</S.Body>
		</UnDraggable>
	);
}

const ContentParser = (content: string): ChatServices.Format2.All => {
	return content
		? JSON.parse(content)
		: {
				type: "richText",
				elements: [
					{
						type: "text",
						text: ""
					}
				]
			};
};

const StyledList = styled(Column.ul)<{ variant: ChatUI.Size }>`
	${hoverScrollCss()};
	width: ${({ variant }) => (variant === "transparent" ? "calc(100% - 8px)" : "100%")};
	clip-path: inset(0 0 0 0);
	overflow-y: auto;
`;
